import React, { useState, useEffect, useCallback } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MapPin, Check, X } from "lucide-react";

const KEY = (username) => `tw1_mijn_locaties_${username || "anon"}`;
const EVT = "tw1-mijn-locaties-changed";

function read(username) {
  try {
    const v = JSON.parse(localStorage.getItem(KEY(username)));
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

// Per-user persisted "my locations" selection, synced across mounted instances.
export function useMyLocations(username) {
  const [selected, setSelectedState] = useState(() => read(username));

  useEffect(() => { setSelectedState(read(username)); }, [username]);

  useEffect(() => {
    const handler = () => setSelectedState(read(username));
    window.addEventListener(EVT, handler);
    window.addEventListener("storage", handler);
    return () => { window.removeEventListener(EVT, handler); window.removeEventListener("storage", handler); };
  }, [username]);

  const setSelected = useCallback((next) => {
    try { localStorage.setItem(KEY(username), JSON.stringify(next)); } catch { /* ignore */ }
    setSelectedState(next);
    window.dispatchEvent(new Event(EVT));
  }, [username]);

  // Returns true when a locatie passes the filter (empty selection = all visible)
  const matches = useCallback((loc) => selected.length === 0 || (loc && selected.includes(loc)), [selected]);

  return { selected, setSelected, matches, active: selected.length > 0 };
}

export function MijnLocatiesSelect({ username, locations, error = false }) {
  const { selected, setSelected, active } = useMyLocations(username);
  const [open, setOpen] = useState(false);

  const toggle = (loc) => {
    setSelected(selected.includes(loc) ? selected.filter((l) => l !== loc) : [...selected, loc]);
  };

  const label = !active ? "Alle locaties" : selected.length === 1 ? selected[0] : `${selected.length} locaties`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="mijn-locaties-trigger"
          className={`flex items-center gap-1.5 px-3 py-2 rounded-md border text-sm font-medium transition-colors ${active ? "bg-blue-50 border-blue-300 text-blue-800" : "bg-white border-zinc-300 text-zinc-500 hover:border-zinc-400"}`}
        >
          <MapPin className="h-4 w-4" />
          <span>{label}</span>
          {active && (
            <span data-testid="mijn-locaties-badge" className="ml-0.5 text-[10px] font-bold bg-blue-600 text-white rounded-full px-1.5 py-0.5 leading-none">{selected.length}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-60 p-0" align="end">
        <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-100">
          <span className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500">Mijn locaties</span>
          {active && (
            <button data-testid="mijn-locaties-reset" onClick={() => setSelected([])} className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800">
              <X className="h-3 w-3" /> Alle
            </button>
          )}
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {(locations || []).length === 0 ? (
            <div data-testid="mijn-locaties-empty" className="px-3 py-4 text-sm text-zinc-500">
              {error ? "Locaties konden niet geladen worden" : "Laden…"}
            </div>
          ) : (locations || []).map((loc) => {
            const on = selected.includes(loc);
            return (
              <button
                key={loc}
                data-testid={`mijn-locaties-option-${loc}`}
                onClick={() => toggle(loc)}
                className="w-full flex items-center justify-between px-3 py-2 text-sm text-zinc-700 hover:bg-zinc-50 text-left"
              >
                <span>{loc}</span>
                <span className={`h-4 w-4 rounded-sm border flex items-center justify-center ${on ? "bg-blue-600 border-blue-600 text-white" : "border-zinc-300"}`}>
                  {on && <Check className="h-3 w-3" />}
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
